import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { nextPoseOrder, setRuntimePoses, usePoses } from '../data/poses';
import { useAuth } from '../auth/AuthContext';
import { navigate } from '../router';
import popular from '../data/popular-poses.json';

const CATEGORIES = [
  'Standing',
  'Seated',
  'Kneeling',
  'Balance',
  'Backbend',
  'Forward Fold',
  'Twist',
  'Inversion',
  'Core',
  'Restorative',
];

const slug = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

interface FormState {
  nameEnglish: string;
  nameSanskrit: string;
  category: string;
  difficultyTier: number;
  hold: string;
  description: string;
  modification: string;
  cuesText: string;
  benefitsText: string;
}

type Phase = 'pick' | 'drafting' | 'editing' | 'saving';

export function NewPosePage() {
  const { user } = useAuth();
  const poses = usePoses();
  const [phase, setPhase] = useState<Phase>('pick');
  const [nameInput, setNameInput] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Curated popular poses minus whatever is already in the catalog.
  const suggestions = useMemo(() => {
    const haveIds = new Set(poses.map((p) => p.id));
    const haveNames = new Set(poses.map((p) => p.nameEnglish.toLowerCase()));
    return (popular as { name: string; sanskrit?: string }[])
      .filter((p) => !haveIds.has(slug(p.name)) && !haveNames.has(p.name.toLowerCase()))
      .slice(0, 24);
  }, [poses]);

  if (!user?.isAdmin) {
    return (
      <div className="empty">
        <p>Adding poses is for admins only.</p>
        <a className="btn" href="#/">
          ← Back to catalog
        </a>
      </div>
    );
  }

  const draft = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    setPhase('drafting');
    try {
      const d = await api.draftPose(trimmed);
      setForm({
        nameEnglish: d.nameEnglish || trimmed,
        nameSanskrit: d.nameSanskrit,
        category: d.category || 'Standing',
        difficultyTier: d.difficultyTier || 1,
        hold: d.hold || '5 full breaths',
        description: d.description,
        modification: d.modification,
        cuesText: d.cues.join('\n'),
        benefitsText: d.benefits.join('\n'),
      });
      setPhase('editing');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not draft that pose.');
      setPhase('pick');
    }
  };

  const startBlank = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setForm({
      nameEnglish: trimmed,
      nameSanskrit: '',
      category: 'Standing',
      difficultyTier: 1,
      hold: '5 full breaths',
      description: '',
      modification: '',
      cuesText: '',
      benefitsText: '',
    });
    setPhase('editing');
  };

  const create = async () => {
    if (!form) return;
    setError(null);
    setPhase('saving');
    try {
      const saved = await api.savePose({
        nameEnglish: form.nameEnglish.trim(),
        nameSanskrit: form.nameSanskrit.trim() || undefined,
        category: form.category,
        difficultyTier: form.difficultyTier,
        description: form.description.trim(),
        cues: form.cuesText.split('\n').map((s) => s.trim()).filter(Boolean),
        benefits: form.benefitsText.split('\n').map((s) => s.trim()).filter(Boolean),
        hold: form.hold.trim() || undefined,
        modification: form.modification.trim() || undefined,
        order: nextPoseOrder(),
      });
      setRuntimePoses(await api.listPoses());
      navigate(`/pose/${saved.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the pose.');
      setPhase('editing');
    }
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  // --- Pick / draft screen ---
  if (phase === 'pick' || phase === 'drafting') {
    return (
      <article className="new-pose">
        <a className="back" href="#/">
          ← Catalog
        </a>
        <h1>Add a pose</h1>
        <p className="muted">
          Pick a popular pose you don't have yet, or type any pose name. The AI drafts the
          details for you to review before it's added.
        </p>

        <div className="np-name-row">
          <input
            type="text"
            className="np-name-input"
            placeholder="e.g. Pigeon Pose"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && draft(nameInput)}
            disabled={phase === 'drafting'}
          />
          <button
            type="button"
            className="btn primary"
            onClick={() => draft(nameInput)}
            disabled={phase === 'drafting' || !nameInput.trim()}
          >
            {phase === 'drafting' ? 'Drafting…' : 'Draft with AI'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => startBlank(nameInput)}
            disabled={phase === 'drafting' || !nameInput.trim()}
          >
            Start blank
          </button>
        </div>
        {error && <p className="auth-error">{error}</p>}
        {phase === 'drafting' && (
          <p className="muted">Drafting the pose details — about 5 seconds…</p>
        )}

        <h3 className="section-label">Popular poses you're missing</h3>
        {suggestions.length === 0 ? (
          <p className="muted">Nice — you already have all the popular poses on our list!</p>
        ) : (
          <div className="np-suggestions">
            {suggestions.map((s) => (
              <button
                key={s.name}
                type="button"
                className="np-suggestion"
                onClick={() => draft(s.name)}
                disabled={phase === 'drafting'}
              >
                <span className="np-sug-name">{s.name}</span>
                {s.sanskrit && <span className="np-sug-sanskrit">{s.sanskrit}</span>}
              </button>
            ))}
          </div>
        )}
      </article>
    );
  }

  // --- Edit / create screen ---
  const f = form!;
  const busy = phase === 'saving';
  return (
    <article className="new-pose">
      <button type="button" className="back" onClick={() => setPhase('pick')}>
        ← Back to suggestions
      </button>
      <h1>Review the new pose</h1>
      <p className="muted">Edit anything below, then add it to your catalog.</p>

      <div className="np-form">
        <label className="np-field">
          <span>Name</span>
          <input value={f.nameEnglish} onChange={(e) => set('nameEnglish', e.target.value)} />
        </label>
        <label className="np-field">
          <span>Sanskrit name</span>
          <input value={f.nameSanskrit} onChange={(e) => set('nameSanskrit', e.target.value)} />
        </label>
        <label className="np-field">
          <span>Category</span>
          <select value={f.category} onChange={(e) => set('category', e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="np-field">
          <span>Difficulty tier</span>
          <select
            value={f.difficultyTier}
            onChange={(e) => set('difficultyTier', Number(e.target.value))}
          >
            <option value={1}>1 — Beginner</option>
            <option value={2}>2 — Intermediate</option>
            <option value={3}>3 — Advanced</option>
          </select>
        </label>
        <label className="np-field">
          <span>Hold</span>
          <input value={f.hold} onChange={(e) => set('hold', e.target.value)} />
        </label>
        <label className="np-field np-wide">
          <span>Description</span>
          <textarea rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} />
        </label>
        <label className="np-field np-wide">
          <span>How to (one step per line)</span>
          <textarea rows={4} value={f.cuesText} onChange={(e) => set('cuesText', e.target.value)} />
        </label>
        <label className="np-field np-wide">
          <span>Benefits (one per line)</span>
          <textarea rows={3} value={f.benefitsText} onChange={(e) => set('benefitsText', e.target.value)} />
        </label>
        <label className="np-field np-wide">
          <span>Modification</span>
          <textarea rows={2} value={f.modification} onChange={(e) => set('modification', e.target.value)} />
        </label>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <div className="np-actions">
        <button type="button" className="btn primary" onClick={create} disabled={busy || !f.nameEnglish.trim()}>
          {busy ? 'Adding…' : 'Add pose to catalog'}
        </button>
        <button type="button" className="btn" onClick={() => setPhase('pick')} disabled={busy}>
          Cancel
        </button>
      </div>
      <p className="muted np-image-note">
        After it's added you'll land on its page, where you can generate its image (and upload a
        reference photo) with the ✨ image editor.
      </p>
    </article>
  );
}
